/**
 * Admin SPA root component with React Router 6 configuration,
 * layout shell with sidebar navigation, top bar, and content area.
 *
 * @module apps/admin/App
 * @see TASK-FRONTEND-ADMIN-SHELL-CHG-001
 */
import React, { useCallback, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useRoutes,
} from "react-router-dom";
import { QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Trans, useLingui } from "@lingui/react/macro";
import { AuthProvider, useAuth } from "../../store/AuthContext";
import {
  IMPERSONATION_DESCRIPTOR_QUERY_KEY,
  exitImpersonationSession,
  impersonationDescriptorQuery,
  impersonationIdentityKey,
  toBannerBinding,
} from "../../api/impersonation";
import { ActiveViewStrip, type ActiveViewStripState } from "../../components/shared/ImpersonationBanner";
import { PermissionProvider, usePermissionContext } from "../../store/PermissionContext";
import { AccessibilityProvider } from "../../contexts/AccessibilityContext";
import { AccessibilityDrawer } from "../../components/accessibility";
import { FeatureGate, ReleaseFeaturesGate } from "../../feature-gate";
import { PlatformNoticeBar } from "../../components/shared/PlatformNoticeBar";
import { AdminSidebar, DRAWER_WIDTH } from "../../components/admin/AdminSidebar";
import { LocalePicker } from "../../components/shared/LocalePicker";
import { LoginPage } from "../../components/shared/LoginPage";
import { ErrorFallback } from "../../components/shared/RouteErrorBoundary";
import { ToastProvider } from "../../components/shared/ToastProvider";
import { buildAdminRoutes } from "./routes";
import { isChunkLoadError, reloadForStaleChunk } from "../../utils/staleChunkRecovery";

// ---------------------------------------------------------------------------
// Environment
// ---------------------------------------------------------------------------

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL || "";

const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || "verisa";

const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || "verisa-admin";

const keycloakUrl = KEYCLOAK_URL
  ? `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}`
  : undefined;

// TanStack Query client (Contract 9: Data Fetching)
import { createQueryClient } from "../../utils/queryClient";
const queryClient = createQueryClient();

// ---------------------------------------------------------------------------
// Error Boundary
// ---------------------------------------------------------------------------

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode; resetKey?: string },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode; resetKey?: string }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error): void {
    console.error("[admin/App] ErrorBoundary caught:", error);
    if (isChunkLoadError(error)) {
      reloadForStaleChunk();
    }
  }

  componentDidUpdate(prevProps: { resetKey?: string }) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error ?? new Error("unknown")}
          onReset={() => this.setState({ hasError: false, error: null })}
        />
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Login route — redirects to admin after successful login
// ---------------------------------------------------------------------------

function AdminLoginRoute(): React.ReactElement {
  const { authenticated } = useAuth();
  const { t } = useLingui();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/";

  if (authenticated) {
    return <Navigate to={from} replace />;
  }

  // THE SUFFIX ONLY, AND NO FOOTER (D-BR-08, D-BR-10, RULE-006a, SCENARIO-015).
  // The product name comes from the BrandingProvider inside LoginPage, which
  // composes "<product name> - Admin" itself (RULE-012); the msgid this mount
  // passes is the app suffix and carries no vendor name. The `footer` prop is
  // DELIBERATELY absent: the platform admin application and its login page carry
  // no legal footer, and the slot's absence is how that is expressed - never a
  // `showFooter={false}`, which would make "admin, but with a footer" a
  // one-character mistake.
  return <LoginPage appSuffix={t`Admin`} />;
}

// ---------------------------------------------------------------------------
// RequireAdminAccess -- permission-based gate (no hardcoded roles)
// ---------------------------------------------------------------------------

interface RequireAdminAccessProps {
  children: React.ReactNode;
}

function RequireAdminAccess({ children }: RequireAdminAccessProps): React.ReactElement {
  const { authenticated, initialized, logout } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissionContext();

  if (!initialized || permissionsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Typography variant="body2" color="text.secondary">
          <Trans>Loading...</Trans>
        </Typography>
      </Box>
    );
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasPermission("platform.admin")) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }} data-testid="access-denied">
        <Typography variant="h5" color="error" gutterBottom>
          <Trans>Access Denied</Trans>
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          <Trans>
            You do not have permission to access the admin panel.
            Only platform administrators can access this application.
          </Trans>
        </Typography>
        <Button variant="outlined" onClick={() => logout()} data-testid="access-denied-logout">
          <Trans>Sign out</Trans>
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Admin Routes (supports nested children via useRoutes)
// ---------------------------------------------------------------------------

function AdminRoutes(): React.ReactElement {
  const routeElements = useRoutes([
    ...buildAdminRoutes(),
    { path: "*", element: <Navigate to="/" replace /> },
  ]);
  return <>{routeElements}</>;
}

// ---------------------------------------------------------------------------
// Admin Layout (authenticated shell)
// ---------------------------------------------------------------------------

function AdminLayout(): React.ReactElement {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  return (
    // The platform notice is a SHELL-level mount: once, outside AdminRoutes, so
    // it renders on every /admin/* route rather than per page, and above the
    // sidebar/content split so it reads as platform-wide rather than
    // sidebar-scoped (SPEC-OPERATIONS RULE-019, TASK-SCR-NOTICE-MOUNT-ADMIN).
    // It renders null whenever no notice is active, which is the ordinary case,
    // so this wrapper costs the common path nothing.
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <PlatformNoticeBar />
      <Box sx={{ display: "flex", flexGrow: 1 }}>
        {/* Top Bar */}
        <AppBar
          position="fixed"
          sx={{
            width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
            ml: { md: `${DRAWER_WIDTH}px` },
          }}
          data-testid="admin-topbar"
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ mr: 2, display: { md: "none" } }}
              data-testid="menu-toggle"
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: 'inherit' }}>
              <Trans>Admin Dashboard</Trans>
            </Typography>
            <Box sx={{ mr: 1 }}>
              <LocalePicker
                compact
                sx={{
                  color: "inherit",
                  "& .MuiSvgIcon-root": { color: "inherit" },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.4)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.7)" },
                }}
              />
            </Box>
            <FeatureGate feature="global.accessibility">
              <AccessibilityDrawer />
            </FeatureGate>
            <IconButton onClick={handleMenuOpen} data-testid="user-menu-button">
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.name?.charAt(0)?.toUpperCase() ?? "?"}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              data-testid="user-menu"
            >
              <MenuItem disabled>
                <Typography variant="body2">{user?.email}</Typography>
              </MenuItem>
              <MenuItem onClick={handleLogout} data-testid="logout-button">
                <Trans>Logout</Trans>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Sidebar */}
        <AdminSidebar
          mobileOpen={mobileOpen}
          onClose={() => setMobileOpen(false)}
        />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
            mt: 8,
          }}
          data-testid="admin-content"
        >
          <ErrorBoundary resetKey={location.pathname}>
            <AdminRoutes />
          </ErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// App Root
// ---------------------------------------------------------------------------

/**
 * MOUNT POINT 3 OF 3 of the identity-keyed remount (SPEC-FRONTEND-RULE-095(2)).
 * The admin SPA is never a landing surface - its gate demands platform.admin,
 * exactly the permission a target lacks - and its AuthProvider never arms the
 * act-as mode (`impersonationDiscovery={false}`). The boundary is still here
 * because the operator returns to this SPA on exit, and a stale permission
 * tree after a chain pop is the same defect. THE BANNER DOES NOT MOUNT HERE:
 * the admin site gets the active-view strip, TASK-IMP-ADMIN-ENTRY's.
 */
export function AdminIdentityBoundary({ children }: { children: React.ReactNode }): React.ReactElement {
  const identityKey = impersonationIdentityKey(useAuth().impersonation ?? null);
  return <React.Fragment key={identityKey}>{children}</React.Fragment>;
}

/**
 * How often the operator's OWN descriptor is re-read on the admin site, in ms.
 * The SAME 60-second cadence the session shell's re-check poll uses
 * (AuthContext `IMPERSONATION_RECHECK_INTERVAL_MS`): the admin AuthProvider
 * runs no discovery (`impersonationDiscovery={false}`), so this read is the
 * admin site's only view of a live binding, and it runs on one TanStack query
 * - the owning module's ONE key and ONE fetcher - rather than a hand-rolled
 * timer.
 */
const ACTIVE_VIEW_POLL_MS = 60_000;

/**
 * THE ACTIVE-VIEW STRIP MOUNT (TASK-IMP-ADMIN-ENTRY-CHG-012). ONE mount, ABOVE
 * ReleaseFeaturesGate and above RequireAdminAccess, for the reason the banner
 * mounts outside every gate: a kill switch or a toggle must never strand a
 * live binding without its exit affordance (RULE-095(1)).
 *
 * It reads the caller's OWN `GET /api/v1/me/impersonation` through the owning
 * module's query options (never the platform-scoped admin list), and its exit
 * is the SELF-EXIT (CONTRACT-039), never the admin revoke.
 */
export function AdminActiveViewStrip(): React.ReactElement | null {
  const { authenticated } = useAuth();
  const queryClient = useQueryClient();
  const descriptor = useQuery({
    ...impersonationDescriptorQuery,
    enabled: authenticated,
    refetchInterval: ACTIVE_VIEW_POLL_MS,
  });
  const [exitState, setExitState] = useState<"idle" | "exiting" | "error">("idle");
  const live = descriptor.data ?? null;
  const liveRef = live?.session_ref ?? null;

  // A different binding (or none) is a fresh start for the exit state.
  React.useEffect(() => {
    setExitState("idle");
  }, [liveRef]);

  const onExit = useCallback(() => {
    if (!live) return;
    setExitState("exiting");
    exitImpersonationSession(live.session_ref)
      .then(() => {
        // 204, 200 {already_ended} and 404 all mean "nothing of mine is live
        // under this reference any more"; re-read rather than assume.
        setExitState("idle");
        queryClient.setQueryData(IMPERSONATION_DESCRIPTOR_QUERY_KEY, null);
        void queryClient.invalidateQueries({ queryKey: IMPERSONATION_DESCRIPTOR_QUERY_KEY });
      })
      .catch(() => setExitState("error"));
  }, [live, queryClient]);

  if (!authenticated) return null;

  let state: ActiveViewStripState;
  if (descriptor.isError && !live) {
    state = "error";
  } else if (!live) {
    state = "none";
  } else if (exitState === "exiting") {
    state = "exiting";
  } else if (exitState === "error") {
    state = "error";
  } else {
    state = "active";
  }

  return <ActiveViewStrip binding={live ? toBannerBinding(live) : null} state={state} onExit={onExit} />;
}

export function AdminApp(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AccessibilityProvider agentId="app">
          <AuthProvider
            keycloakUrl={keycloakUrl}
            clientId={KEYCLOAK_CLIENT_ID}
            impersonationDiscovery={false}
          >
            <AdminIdentityBoundary>
              <PermissionProvider>
                <ToastProvider>
                  <AdminActiveViewStrip />
                  <ReleaseFeaturesGate>
                    <Routes>
                      <Route path="/login" element={<AdminLoginRoute />} />
                      <Route path="/*" element={<RequireAdminAccess><AdminLayout /></RequireAdminAccess>} />
                    </Routes>
                  </ReleaseFeaturesGate>
                </ToastProvider>
              </PermissionProvider>
            </AdminIdentityBoundary>
          </AuthProvider>
        </AccessibilityProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default AdminApp;
